import { RotatingLines } from "react-loader-spinner";
import ItemImage from "../../../assets/images/productimage.png";
import {
  EMPTY_STRING,
  inventoryConstants,
  toggleSwitchButtonConstants,
} from "../../../constants";
import {
  PRODUCT_TYPES_ENUMS,
  TRADING_CARD_CATEGORIES_ENUMS,
} from "../../../system/globalEnums";
import SimpleNumberField from "../../components/inputNumberField/SimpleNumberField";
import SelectGrade from "../../components/inventory/SelectGrade";
import SelectDropDown from "../../components/selectDropdown/SelectDropdown";
import TableCustomHeader from "../../components/table/TableCustomHeader";
import {
  batchCostOfGoodsChange,
  batchStockPriceChange,
  getCogsValue,
  getQuantityValue,
  handleBatchProductDelete,
  handleCalculateEachProductPercentage,
  handleValueUpdateInBatchModalRow,
  isStoreCustomProduct,
  onChangeProductQuantity,
} from "../inventoryUtility";
import {
  excludeNullColumns,
  getInvStockPrice,
  getActionColumnOfReactDataTableComponent,
  getProductIfExist,
  getProductSubCategory,
  getShortestSku,
  handleLockOrUnlockTradeItem,
  handleShowNintendoCondition,
  isMarketPriceHigh,
  isSystemProduct,
  onChangeProductCondition,
  parseToDecimalNumber,
  parseToThousandSeperatorDecimalNumber,
} from "../../utility";
import {
  getTradingCardConditionListForSelectedGrade,
  getTradingCardMarketPriceForSelectedProperties,
} from "../thirdPartyProductsApiUtility";
import {
  DeleteIcon,
  EditIcon,
  LockIcon,
  UnLockIcon,
} from "../../../assets/icons/iconsProvider";
import SelectCardRarity from "../../components/inventory/SelectCardRarity";
import { CustomCellRendererWithSkeleton, TitleToolTip } from "../../components";

const getSkuColumnWithImage = () => {
  return {
    headerName: "Sku",
    lockPosition: "left",
    field: "sku",
    cellStyle: {},
    minWidth: 180,
    cellRenderer: (row) => {
      const shortestSku = getShortestSku(row.data.sku);
      return (
        <CustomCellRendererWithSkeleton
          params={row}
          componentRenderCondition={row?.data?.sku}
          cellComponent={
            <div className="d-flex justify-content-start align-items-center">
              <div className="product-image">
                <img
                  src={row.data.imgUrl || ItemImage}
                  alt={`${
                    row.data.productName ||
                    row.data.product_name ||
                    "Product image"
                  }`}
                />
              </div>
              <TitleToolTip title={row.data.sku} children={shortestSku} />
            </div>
          }
        />
      );
    },
    checkboxSelection: true,
    headerCheckboxSelection: true,
  };
};

const getProductNameColumn = (handleEditClick = () => {}) => {
  return {
    headerTooltip: "Product Name",
    headerName: "Product Name",
    field: "product_name",
    maxWidth: undefined,
    cellRenderer: (params) => (
      <CustomCellRendererWithSkeleton
        params={params}
        titleToolTipText={params?.data?.product_name}
        componentRenderCondition={params?.data?.product_name}
        cellComponent={
          <span
            className=" product-edit-anchor-link cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick(params.data);
            }}
          >
            {params?.data?.product_name}
          </span>
        }
      />
    ),
  };
};

const getProductTypeColumn = () => {
  return {
    headerName: "Type",
    field: "productType",
    cellRenderer: (row) => (
      <CustomCellRendererWithSkeleton
        params={row}
        componentRenderCondition={row?.data?.productType}
        cellComponent={row.data.productType}
      />
    ),
  };
};

const getProductCategoryColumn = () => {
  return {
    headerName: "Category",
    field: "category_name",
    cellRenderer: (params) => (
      <CustomCellRendererWithSkeleton
        params={params}
        componentRenderCondition={params?.data?.category_name}
        cellComponent={params?.data?.category_name}
      />
    ),
  };
};

const getProductConditionColumn = () => {
  return {
    headerName: "Condition",
    field: "price.type",
    cellRenderer: (params) => (
      <CustomCellRendererWithSkeleton
        params={params}
        componentRenderCondition={params?.data?.price?.type}
        cellComponent={params?.data?.price?.type}
      />
    ),
  };
};

const getProductTagColumn = () => {
  return {
    headerName: "Tags",
    field: "tags",
    cellRenderer: (params) => (
      <CustomCellRendererWithSkeleton
        params={params}
        componentRenderCondition={params?.data?.tags}
        cellComponent={params?.data?.tags?.length}
        titleToolTipText={params?.data?.tags}
      />
    ),
  };
};

// Same column used in inventory, invoice and trade modals
const getBatchModalGradeColumn = (
  addBatchInventory,
  setBatchInventory,
  storeId
) => {
  // Only render grade column if there's one product with product type trading card
  return (
    addBatchInventory.some(
      (product) => product.productType === PRODUCT_TYPES_ENUMS.TRADING_CARD
    ) && {
      id: "grade",
      name: "Grade",
      selector: (row) => row.grade,
      cell: (row) => {
        return (
          <SelectGrade
            isFormik={false}
            value={row.grade}
            priceChartingProductInfo={row}
            setFieldValue={(...e) => {
              // handle update grade, condition list based on selected grade, default selected condition and setBatch in reducer
              let conditionList = getTradingCardConditionListForSelectedGrade(
                e[1],
                row.subcategory
              );
              handleValueUpdateInBatchModalRow(
                row,
                ["grade", "productConditionList", "productCondition"],
                [e[1], conditionList, conditionList[0].value],
                addBatchInventory,
                setBatchInventory,
                storeId
              );
            }}
          />
        );
      },
      sortable: false,
      sortDirection: null,
      width: "8%",
    }
  );
};

// Same column used in inventory and trade modals
const getBatchModalRarityColumn = (
  addBatchInventory,
  setAddBatchInventory,
  storeId,
  markupPresets,
  tradeinMargin
) => {
  const handleOnChangeCardRarityInBatchModalRow = (e, row) => {
    const cardRarityValue = e[1];
    // Update cardRarity, marketPrice
    const marketPrice = getTradingCardMarketPriceForSelectedProperties(
      row,
      undefined,
      undefined,
      cardRarityValue
    );
    handleValueUpdateInBatchModalRow(
      row,
      ["cardRarity", "marketPrice"],
      [cardRarityValue, marketPrice],
      addBatchInventory,
      setAddBatchInventory,
      storeId
    );
  };

  // Only render Printing column if there's one product with product type trading card
  return (
    addBatchInventory.some(
      (product) => product.productType === PRODUCT_TYPES_ENUMS.TRADING_CARD
    ) && {
      id: "cardRarity",
      name: "Printing",
      selector: (row) => row.cardRarity,
      cell: (row) => {
        return (
          <SelectCardRarity
            isFormik={false}
            value={row.cardRarity}
            priceChartingProductInfo={row}
            tradeinMargin={tradeinMargin}
            markupPresets={markupPresets}
            setFieldValue={(...e) => {
              handleOnChangeCardRarityInBatchModalRow(e, row);
            }}
          />
        );
      },
      sortable: false,
      sortDirection: null,
      width: "8%",
    }
  );
};

// Same column used in inventory and invoice modals
const getBatchModalQuantityColumn = (
  currentStore,
  addBatchInventory,
  setAddBatchInventory
) => {
  return {
    id: "quantity",
    name: "Quantity",
    selector: (row) => getQuantityValue(row, false),
    cell: (row) => {
      return (
        <SimpleNumberField
          step={1}
          minimumValue={1}
          key={row.id}
          value={getQuantityValue(row, false)}
          isConvertToDecimal={false}
          className={`minimum-number-field max-input-width w-100 trade-batch-input-fields`}
          maxValue={inventoryConstants.THRESHOLD_MAX_CASH_VALUE}
          disabled={
            (isSystemProduct(getProductSubCategory(row?.genre)) &&
              currentStore[
                toggleSwitchButtonConstants.IS_SERIAL_NUMBER_REQUIRED_INVENTORY
              ]) ||
            row?.serialNumber ||
            row?.isItemLocked ||
            (!row.trackQuantity && isStoreCustomProduct(row, "apiSource"))
          }
          onChange={(value) => {
            if (value !== 0) {
              onChangeProductQuantity(
                row.id,
                value,
                addBatchInventory,
                setAddBatchInventory,
                handleCalculateEachProductPercentage
              );
            }
          }}
          isHandleKeyDown={true}
        />
      );
    },
    sortable: false,
    sortDirection: null,
    width: "8%",
  };
};

// Same column used in inventory and invoice modals
const getBatchModalCogsColumn = (
  addBatchInventory,
  setAddBatchInventory,
  handleEmptyOnFocus = () => {}
) => {
  return {
    id: "cogs",
    name: "COGS",
    selector: (row) => getCogsValue(row, false, false),
    cell: (row) => {
      return (
        <input
          key={getCogsValue(row, false, false)}
          min={0}
          type="number"
          defaultValue={getCogsValue(row, false, false)}
          onFocus={handleEmptyOnFocus}
          className="input-textfield editable-inventory-cell-max-size trade-batch-input-fields"
          onBlur={(e) => {
            const inputValue = parseToDecimalNumber(e.target.value); // Convert to number
            batchCostOfGoodsChange(
              row.id,
              inputValue,
              addBatchInventory,
              setAddBatchInventory
            );
          }}
          disabled={row?.isItemLocked}
        ></input>
      );
    },
    sortable: false,
    sortDirection: null,
    width: "8%",
  };
};

const getModalStockPriceColumn = (
  currentStore,
  addBatchInventory,
  setAddBatchInventory,
  productTags,
  handleEmptyOnFocus = () => {},
  markupPresets,
  columnOptions = {}
) => {
  return {
    id: "stockPrice",
    name: "Stock Price",
    selector: (row) => getInvStockPrice(row, false, false),
    cell: (row) => {
      return (
        <input
          key={getInvStockPrice(row, false, false)}
          min={0}
          type="number"
          // value={row.inStockPrice}
          defaultValue={getInvStockPrice(row, false, false)}
          onFocus={handleEmptyOnFocus}
          // onChange={(e) => onChangeStockPrice(e.target.value, row.id)}
          className={`input-textfield editable-inventory-cell-max-size trade-batch-input-fields ${
            isMarketPriceHigh(
              row.marketPrice,
              getInvStockPrice(row, false, false),
              productTags,
              row.tags.map((tag) => tag.label),
              row.isDataFetching
            )
              ? "border-danger"
              : ""
          }`}
          onBlur={(e) => {
            const inputValue = parseToDecimalNumber(e.target.value); // Convert to number
            const prevValue = parseToDecimalNumber(
              row?.isSeparateQuantity && row?.isOnlineUnitSellPrice
                ? row?.onlineUnitSellPrice
                : row.inStockPrice
            );
            inputValue !== prevValue &&
              batchStockPriceChange(
                row.id,
                inputValue,
                currentStore,
                markupPresets,
                addBatchInventory,
                setAddBatchInventory
              );
          }}
          disabled={row?.isItemLocked}
        ></input>
      );
    },
    sortable: false,
    sortDirection: null,
    ...columnOptions,
  };
};

const getAddBatchInventoryColumns = ({
  productTags,
  currentStore,
  markupPresets,
  tradeinMargin,
  addBatchInventory,
  handleEmptyOnFocus,
  handleEditCardRowClick,
  setAddBatchInventory,
  onChangeProductQuantity,
}) => {
  let columns = [
    {
      id: "title",
      name: "Title",
      selector: (row) => row.productName,
      cell: (row) => {
        return (
          <div className="d-flex flex-column">
            <TableCustomHeader
              id={`tool${row.id}`}
              label={
                <span className="inventory-cell-max-size product-title-max-width">
                  {row.productName}
                </span>
              }
              toolTipText={row.productName}
            />
            {getProductSubCategory(row.genre) ===
              TRADING_CARD_CATEGORIES_ENUMS.SYSTEM &&
              (currentStore[
                toggleSwitchButtonConstants.IS_SERIAL_NUMBER_REQUIRED_INVENTORY
              ] ||
                row?.serialNumber) && (
                <span
                  className={`${
                    row.serialNumber ? EMPTY_STRING : "text-danger"
                  } batch-seial-number-label`}
                >
                  S/N : {row?.serialNumber || "Required"}
                </span>
              )}
          </div>
        );
      },
      sortable: false,
      sortDirection: null,
    },
    getBatchModalGradeColumn(addBatchInventory, setAddBatchInventory),
    {
      id: "condition",
      name: "Condition",
      selector: (row) => row.productCondition,
      cell: (row) => {
        return (
          <SelectDropDown
            menuPlacement="bottom"
            options={row.productConditionList}
            noOptionsMessage="No Type Found"
            placeHolder="Type"
            defaultPageSize={{
              value: row?.productCondition,
              label: handleShowNintendoCondition(
                row.consoleName,
                row?.productCondition
              ),
            }}
            handleChange={(e) =>
              onChangeProductCondition(
                markupPresets,
                row.id,
                e.value,
                false,
                addBatchInventory,
                setAddBatchInventory,
                tradeinMargin
              )
            }
            isDisabled={row?.isItemLocked}
            menuPortalTarget=""
            value={{
              value: row?.productCondition,
              label: handleShowNintendoCondition(
                row.consoleName,
                row?.productCondition
              ),
            }}
          />
        );
      },
      sortable: false,
      sortDirection: null,
    },
    getBatchModalRarityColumn(
      addBatchInventory,
      setAddBatchInventory,
      currentStore?.id,
      markupPresets,
      tradeinMargin
    ),
    {
      id: "type",
      name: "Type",
      selector: (row) => row.productType,
      cell: (row) => {
        return (
          <span className="inventory-cell-max-size">{row.productType}</span>
        );
      },
      sortable: false,
      sortDirection: null,
      width: "8%",
    },
    {
      id: "category",
      name: "Category",
      selector: (row) => row.consoleName,
      cell: (row) => {
        return (
          <span className="inventory-cell-max-size">{row.consoleName}</span>
        );
      },
      sortable: false,
      sortDirection: null,
    },
    {
      id: "marketPrice",
      name: "Market Price",
      selector: (row) => row.marketPrice,
      cell: (row) => {
        return (
          <span
            className={`inventory-cell-max-size ${
              isMarketPriceHigh(
                row.marketPrice,
                row.inStockPrice,
                productTags,
                row.tags.map((tag) => tag.label),
                row.isDataFetching
              )
                ? "text-danger"
                : ""
            }`}
          >
            {parseToThousandSeperatorDecimalNumber(row.marketPrice)}
          </span>
        );
      },
      sortable: false,
      sortDirection: null,
      width: "8%",
    },
    getBatchModalQuantityColumn(
      currentStore,
      addBatchInventory,
      setAddBatchInventory
    ),
    getBatchModalCogsColumn(
      addBatchInventory,
      setAddBatchInventory,
      handleEmptyOnFocus
    ),
    getModalStockPriceColumn(
      currentStore,
      addBatchInventory,
      setAddBatchInventory,
      productTags,
      handleEmptyOnFocus,
      markupPresets,
      { width: "8%" }
    ),
    {
      id: "inStockPriceAnd",
      name: "",
      cell: (row) => {
        return getProductIfExist(row, (value, id) =>
          batchStockPriceChange(
            id,
            value,
            currentStore,
            markupPresets,
            addBatchInventory,
            setAddBatchInventory
          )
        );
      },
      sortable: false,
      width: "7%",
    },
    getActionColumnOfReactDataTableComponent((row) => {
      return row?.isDataFetching ? (
        <div className="d-flex justify-content-center align-items-center">
          <RotatingLines
            visible={true}
            height="20"
            width="20"
            color="grey"
            strokeColor="#6a18ed"
            strokeWidth="4"
            animationDuration="0.75"
            ariaLabel="rotating-lines-loading"
          />
        </div>
      ) : (
        <div className="d-flex justify-content-end align-items-end gap-2">
          <button
            id={`modalTool${row.id}`}
            className="action-Button-Wrapper"
            onClick={() =>
              handleBatchProductDelete(
                row.id,
                addBatchInventory,
                setAddBatchInventory
              )
            }
          >
            <DeleteIcon className="cursor-pointer text-primary" />
          </button>
          <button
            id={`modalTool${row.id}`}
            className="action-Button-Wrapper"
            onClick={() => {
              handleLockOrUnlockTradeItem(
                currentStore,
                addBatchInventory,
                setAddBatchInventory,
                row.id
              );
            }}
          >
            {row?.isItemLocked ? (
              <LockIcon className="cursor-pointer text-primary" />
            ) : (
              <UnLockIcon className="cursor-pointer " />
            )}
          </button>
          <button
            id={`modalTool${row.id}`}
            className="action-Button-Wrapper ps-0 pe-0"
            onClick={() => {
              handleEditCardRowClick(row);
            }}
          >
            <EditIcon className="cursor-pointer text-primary" />
          </button>
        </div>
      );
    }),
  ];
  let filteredColumns = excludeNullColumns(columns);
  return filteredColumns;
};

export {
  getProductTagColumn,
  getProductNameColumn,
  getProductTypeColumn,
  getSkuColumnWithImage,
  getBatchModalGradeColumn,
  getProductCategoryColumn,
  getBatchModalRarityColumn,
  getProductConditionColumn,
  getAddBatchInventoryColumns,
  getBatchModalQuantityColumn,
  getBatchModalCogsColumn,
  getModalStockPriceColumn,
};
